import React, { useState, useEffect } from 'react';
import { Star, X, ExternalLink, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

/**
 * GoogleReviewPrompt — Asks the user for a Google review after completing 3 tests.
 *
 * Behaviour:
 * - Triggered by a parent that increments `completed_tests` counter in localStorage.
 * - Shows once at threshold=3 and then with a longer cool-down on "later".
 * - "Don't show again" disables it permanently.
 *
 * The Google review URL is loaded from the public site settings endpoint so admins
 * can change it without redeploys.
 */
const GoogleReviewPrompt = ({ open, onClose, googleReviewUrl, score, ar }) => {
  const { t, i18n } = useTranslation();
  const isAr = ar ?? i18n.language === 'ar';
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);

  if (!open) return null;

  const handleNotNow = () => {
    // Snooze: ask again after the user completes 3 more tests
    localStorage.setItem('reviewPromptSnoozeUntil', String(Date.now() + 1000 * 60 * 60 * 24 * 7));
    onClose('later');
  };

  const handleNever = () => {
    localStorage.setItem('reviewPromptDismissedForever', '1');
    onClose('never');
  };

  const handleReview = () => {
    if (selected >= 4 && googleReviewUrl) {
      window.open(googleReviewUrl, '_blank', 'noopener,noreferrer');
      localStorage.setItem('reviewPromptCompleted', '1');
      onClose('reviewed');
    } else if (selected > 0 && selected <= 3) {
      // Low-rating users: send feedback to support instead — never to Google
      localStorage.setItem('reviewPromptLowRated', '1');
      onClose('low-rating');
    }
  };

  const labels = {
    title: { ar: 'كيف كانت تجربتك؟', en: 'How was your experience?', sv: 'Hur var din upplevelse?', tr: 'Deneyiminiz nasıldı?', fa: 'تجربه شما چگونه بود؟', so: 'Sidee bay ahayd waayo-aragnimadaadu?', pl: 'Jak było twoje doświadczenie?', es: '¿Qué tal tu experiencia?', de: 'Wie war deine Erfahrung?' },
    sub: {
      ar: 'تقييمك يساعد آلاف الطلاب في إيجاد منصة موثوقة',
      en: 'Your review helps thousands of students find a trusted platform',
      sv: 'Din recension hjälper tusentals elever att hitta en pålitlig plattform',
      tr: 'Yorumunuz binlerce öğrencinin güvenilir bir platform bulmasına yardımcı olur',
      fa: 'بازخورد شما به هزاران دانشجو کمک می‌کند پلتفرمی قابل اعتماد پیدا کنند',
      so: 'Dib-u-eegistaadu waxay ka caawisaa kumanaan arday inay helaan barxad la aamini karo',
      pl: 'Twoja recenzja pomaga tysiącom uczniów znaleźć zaufaną platformę',
      es: 'Tu reseña ayuda a miles de estudiantes a encontrar una plataforma confiable',
      de: 'Deine Bewertung hilft Tausenden von Schülern eine vertrauenswürdige Plattform zu finden',
    },
    leaveReview: { ar: 'اكتب مراجعة على Google', en: 'Leave a Google Review', sv: 'Lämna en Google-recension', tr: 'Google İncelemesi Yaz', fa: 'نظر در گوگل بنویسید', so: 'Ku qor Google', pl: 'Zostaw recenzję Google', es: 'Dejar reseña en Google', de: 'Google-Bewertung schreiben' },
    sendFeedback: { ar: 'إرسال ملاحظات', en: 'Send Feedback', sv: 'Skicka feedback', tr: 'Geri Bildirim Gönder', fa: 'ارسال بازخورد', so: 'Soo dir faallo', pl: 'Wyślij opinię', es: 'Enviar comentario', de: 'Feedback senden' },
    later: { ar: 'لاحقاً', en: 'Maybe later', sv: 'Kanske senare', tr: 'Belki sonra', fa: 'بعداً', so: 'Mar dambe', pl: 'Może później', es: 'Más tarde', de: 'Später' },
    never: { ar: 'لا تسأل مرة أخرى', en: "Don't ask again", sv: 'Fråga inte igen', tr: 'Bir daha sorma', fa: 'دیگر نپرس', so: 'Ha i weydiin mar dambe', pl: 'Nie pytaj ponownie', es: 'No preguntar de nuevo', de: 'Nicht mehr fragen' },
    rate: { ar: 'قيّم تجربتك', en: 'Rate your experience', sv: 'Betygsätt din upplevelse', tr: 'Deneyiminizi değerlendirin', fa: 'به تجربه خود امتیاز دهید', so: 'Qiimee waayo-aragnimadaada', pl: 'Oceń swoje doświadczenie', es: 'Califica tu experiencia', de: 'Bewerte deine Erfahrung' },
  };
  const lang = i18n.language || 'en';
  const L = (k) => labels[k][lang] || labels[k].en;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="google-review-prompt-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleNotNow(); }}
    >
      <div
        className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        data-testid="google-review-prompt"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* Close button */}
        <button
          onClick={handleNotNow}
          className="absolute top-3 end-3 z-10 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="review-close-btn"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>

        {/* Decorative header */}
        <div className="relative bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-400 dark:from-amber-500 dark:via-orange-500 dark:to-rose-500 px-6 pt-8 pb-12 text-center">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, white 1px, transparent 1px), radial-gradient(circle at 70% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/95 shadow-lg mb-3">
            <Heart className="w-10 h-10 text-rose-500" fill="currentColor" />
          </div>
          <h2 className="text-2xl font-black text-white drop-shadow-md">{L('title')}</h2>
          {typeof score === 'number' && (
            <p className="mt-1 text-sm font-bold text-white/95">
              {isAr ? `لقد أحرزت ${score}%` : `You scored ${score}%`}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="px-6 -mt-6 relative">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-lg border border-slate-100 dark:border-slate-700">
            <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed mb-4">
              {L('sub')}
            </p>

            {/* Star rating */}
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 text-center uppercase tracking-wide mb-2">{L('rate')}</p>
            <div className="flex items-center justify-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onMouseEnter={() => setHovered(n)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setSelected(n)}
                  className="transition-transform hover:scale-110 active:scale-95"
                  data-testid={`review-star-${n}`}
                >
                  <Star
                    className={`w-9 h-9 ${(hovered || selected) >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-600'} transition-colors`}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {/* CTA based on rating */}
            {selected >= 4 && googleReviewUrl && (
              <Button
                onClick={handleReview}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold rounded-xl shadow-md flex items-center justify-center gap-2"
                data-testid="review-submit-google-btn"
              >
                <ExternalLink className="w-4 h-4" />
                {L('leaveReview')}
              </Button>
            )}
            {selected > 0 && selected < 4 && (
              <Button
                onClick={handleReview}
                className="w-full h-12 bg-slate-700 hover:bg-slate-800 text-white font-bold rounded-xl shadow-md"
                data-testid="review-submit-feedback-btn"
              >
                {L('sendFeedback')}
              </Button>
            )}
            {selected === 0 && (
              <Button
                disabled
                className="w-full h-12 bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed rounded-xl"
              >
                {L('rate')}
              </Button>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between gap-3 px-2 py-4">
            <button
              onClick={handleNotNow}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              data-testid="review-later-btn"
            >
              {L('later')}
            </button>
            <button
              onClick={handleNever}
              className="text-xs font-medium text-slate-400 hover:text-rose-500 transition-colors"
              data-testid="review-never-btn"
            >
              {L('never')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook that determines when to show the prompt based on completed tests.
 * Returns { open, close, googleReviewUrl, score }.
 */
export const useGoogleReviewPrompt = ({ apiBase, scoreThreshold = 60 } = {}) => {
  const [open, setOpen] = useState(false);
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [score, setScore] = useState(undefined);

  // Fetch the configured URL once
  useEffect(() => {
    if (!apiBase) return;
    fetch(`${apiBase}/site/settings/public`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.google_review_url) setGoogleReviewUrl(d.google_review_url);
      })
      .catch(() => {});
  }, [apiBase]);

  // Trigger after a test completes
  const triggerAfterTest = (testScore) => {
    if (localStorage.getItem('reviewPromptDismissedForever')) return;
    if (localStorage.getItem('reviewPromptCompleted')) return;
    const snoozeUntil = parseInt(localStorage.getItem('reviewPromptSnoozeUntil') || '0', 10);
    if (snoozeUntil > Date.now()) return;

    const completedKey = 'completedTestsCount';
    const count = parseInt(localStorage.getItem(completedKey) || '0', 10) + 1;
    localStorage.setItem(completedKey, String(count));

    // Trigger after 3 completed tests AND a decent score (avoid frustrated low-scorers)
    if (count >= 3 && (testScore == null || testScore >= scoreThreshold)) {
      setScore(testScore);
      setOpen(true);
      // Reset count so it can re-trigger after another 3 tests if user clicks "later"
      localStorage.setItem(completedKey, '0');
    }
  };

  const close = () => setOpen(false);

  return { open, close, googleReviewUrl, score, triggerAfterTest };
};

export default GoogleReviewPrompt;
